import React, { useEffect, useState } from "react";
import {
    useTheme,
    Grid,
    Dialog,
    DialogContent,
    IconButton,
    DialogTitle,
    Button,
    Typography,
    Divider,
    Tooltip,
} from "@mui/material";

import { Close, ContentCopy, Language, TaskAltOutlined, TipsAndUpdates } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSubmitReferralMutation } from "Services/ReferralService";
import { Box, useMediaQuery } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { showErrorToaster, showSuccessToaster } from "store/Shared.Slice";

import referCharityImg from "assets/images/referral/refer_charity.svg";
import FormControlWrapper from "components/FormControlWrapper";
import MaskedInputWrapper from "components/MaskedInputWapper";


const ReferCharityPopup = ({ openPopup, onClose }) => {

    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { userToken } = useSelector((state) => state.sharedState);

    const matchDown1 = useMediaQuery('(max-height:1000px)');
    const matchDown2 = useMediaQuery('(max-height:900px)');
    

    const [submitReferral, { isLoading: isReferralSubmitting }] = useSubmitReferralMutation();

    const [formFields, setFormFields] = useState({
        charityName: {
            value: ' ',
            error: ''
        },
        contactName: {
            value: '',
            error: ''
        },
        phoneNumber: {
            value: '',
            error: ''
        },
        email: {
            value: '',
            error: ''
        },
    });

    const [ showReferMsg, setShowReferMsg ] = useState(true);

    const handleUpdate = (field, value) => {
        setFormFields({
            ...formFields,
            [field]: {
                value,
                error: ''
            }
        });
    }

    const handleCopyToClipboard = () => {

        const referUrl = `${window.location.origin}/register?referral=${userToken?.UserName}`;

        navigator.clipboard.writeText(referUrl).then(() => {
            dispatch(
                showSuccessToaster(
                    `Copied to clipboard. ${referUrl}`
                )
            );
        });
    };

    const handleSubmitReferral = async () => {
        try {
            const model = {
                nameCharity: formFields.charityName.value || null,
                nameContact: formFields.contactName.value || null,
                email: formFields.email.value || null,
                phoneNumber: formFields.phoneNumber.value || null,
            };

            const response = await submitReferral(model).unwrap();

            dispatch(showSuccessToaster(response.message));
            onClose();

        } catch (error) {
            // Model Errors
            if (error.data?.errors != null) {
                const copyFormValue = { ...formFields };
                Object.keys(error.data?.errors).forEach((key) => {
                    if (copyFormValue[key]) {
                        copyFormValue[key].error = error.data?.errors[key][0];
                    }
                });

                setFormFields({
                    ...formFields,
                    ...copyFormValue
                });

                dispatch(showErrorToaster(t('errors.model_error')));
            }
            // Bad Request
            else if (error.data?.message != null) {
                dispatch(showErrorToaster(error.data?.message));
            }
        }
    };

    const handleClear = () => {
        setShowReferMsg(true);

        setFormFields({
            ...formFields,
            charityName: {
                value: '',
                error: ''
            },
            contactName: {
                value: '',
                error: ''
            },
            phoneNumber: {
                value: '',
                error: ''
            },
            email: {
                value: '',
                error: ''
            }
        });
    }

    useEffect(() => {
        handleClear();
    }, [openPopup])

    return (
        <Dialog
            open={openPopup}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                zoom: matchDown2 ? '0.8' :
                      matchDown1 ? '0.9' :
                      1
            }}
        >
             <DialogTitle
                sx={{
                    fontSize: "1rem",
                    pt: 4,
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 20,
                    }}
                >
                    <Close />
                </IconButton>
             
            </DialogTitle>

            <DialogContent sx={{ borderTop: 0 }}>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <img
                                src={referCharityImg}
                                alt="img"
                                height={140}
                            />
                        </div>
                    </Grid>

                    <Grid item xs={12} textAlign="center" display={showReferMsg ? 'block' : 'none'}>
                        <Box
                            p={1.5}
                            sx={[
                                { bgcolor: "grey.100", borderRadius: 2 },
                                theme.applyStyles("dark", {
                                    bgcolor: "background.paper2",
                                }),
                            ]}
                            mb={1}
                            display="flex"
                            alignItems="center"
                        >
                            <TipsAndUpdates color="warning" sx={{ mr: 1 }} />
                            <Typography variant="caption">
                                {t("labels.referralFormPopup")}
                            </Typography>
                            <IconButton
                                aria-label="close"
                                size="small"
                                onClick={() => setShowReferMsg(false)}
                                sx={{
                                    position: 'absolute',
                                    right: '20px',
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={2} mb={3} textAlign="center">
                    <Grid item xs={12}>
                       <span style={{ fontWeight: 'bold', fontSize: '19px' }}>{t('labels.charities_helping_charities_title')}</span>
                    </Grid>
                    <Grid item xs={12} >
                       <Typography variant="label" fontSize={18}>{t('labels.refer_charity_earn_bonus')}</Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                       <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TaskAltOutlined color="secondary" />
                            <span style={{ marginLeft: '10px' }}>{t('messages.referral_bonus_1')}</span>
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                       <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TaskAltOutlined color="secondary" />
                            <span style={{ marginLeft: '10px' }}>{t('messages.referral_bonus_2')}</span>
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                       <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TaskAltOutlined color="secondary" />
                            <span style={{ marginLeft: '10px' }}>{t('messages.referral_bonus_3')}</span>
                        </div>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={1}>
                    <Grid item xs={6}>
                       <FormControlWrapper 
                            value={formFields.charityName.value}
                            label={t('labels.charity_name')}
                            error={formFields.charityName.error}
                            theme={theme}
                            required
                            onChange={(e) => handleUpdate("charityName", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6}>
                       <FormControlWrapper 
                            value={formFields.contactName.value}
                            label={t('labels.name_of_contact')}
                            error={formFields.contactName.error}
                            theme={theme}
                            required
                            onChange={(e) => handleUpdate("contactName", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6} mt={-2}>
                       <MaskedInputWrapper 
                            value={formFields.phoneNumber.value}
                            label={t('labels.phone_number')}
                            error={formFields.phoneNumber.error}
                            theme={theme}
                            maskType="phone" 
                            required
                            onChange={(e) => handleUpdate("phoneNumber", e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={6} mt={-2}>
                       <FormControlWrapper 
                            value={formFields.email.value}
                            label={t('labels.email_address')}
                            error={formFields.email.error}
                            theme={theme}
                            required
                            onChange={(e) => handleUpdate("email", e.target.value)}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={0.1} mb={2}>
                    <Grid item xs={12} textAlign="center">
                        <LoadingButton
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={handleSubmitReferral}
                            loading={isReferralSubmitting}
                        >
                            {t('buttons.submit')}
                        </LoadingButton>
                    </Grid>
                </Grid>

                <Divider>{t("labels.or")}</Divider>

                <Grid container spacing={2} textAlign="center" mt={0.1} mb={2}>
                    <Grid item xs={12}>
                        <span style={{ fontFamily: 'Poppins', fontWeight: '500', fontSize: '18px' }}>{t('labels.referralShareFormPopup')}</span>
                    </Grid>

                    <Grid item xs={12}>

                        <Tooltip
                            title={t("labels.referral_code_tooltip")}
                            arrow
                        >
                            <Button 
                                color="primary"
                                startIcon={<Language />}
                                endIcon={<ContentCopy />}
                                variant="text"
                                onClick={handleCopyToClipboard}
                            >
                                {t('labels.referral_code')}
                            </Button>
                        </Tooltip>

                    </Grid>
                </Grid>

            </DialogContent>

        </Dialog>
    );
};

export default ReferCharityPopup;
